import { Dictionary } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Role } from 'api/models';
import { Button } from 'components/atoms/button';
import { useTranslate } from 'hooks/use-translation';
import { useResourceAvailabilityForTeam } from 'queries/resource-management';
import {
  ProjectsWithMembers,
  ReplacementUserForTeam,
  TeamLite,
} from '../types';
import { ignoreMinutesIfZero } from '../utils/dates';
import {
  ModalContentLoader,
  ModalTitle,
  AddToProjectModalFooter as Footer,
  ModalWithStyledFooter,
} from './common';
import { FindMemberContent } from './modal-content/find-member-content';
import { useCheckRoleAndReplaceUser } from './use-check-role-and-replace-user';
import { useReplaceTeamWithUser } from './use-replace-team-with-user';

interface Props {
  teamData: TeamLite;
  onClose: () => void;
  projectLookup: Dictionary<ProjectsWithMembers>;
  roleLookup: Dictionary<Role>;
}

export const FindMemberModal: FC<Props> = ({
  teamData,
  onClose,
  projectLookup,
  roleLookup,
}) => {
  const [selectedReplacementUser, setSelectedReplacementUser] =
    useState<ReplacementUserForTeam>();

  const translate = useTranslate();

  const { projectId, teamId, teamName, roleId } = teamData;
  const teamRoleName = roleId ? roleLookup[roleId]?.roleName : undefined;
  const { projectName } = projectLookup[projectId];

  const { data: availableResources, isLoading } =
    useResourceAvailabilityForTeam(projectId, teamId);

  const secondaryTitle = useMemo(
    () =>
      teamName !== '' && projectName !== ''
        ? `${teamName} | ${projectName}${
            availableResources?.requiredMinutes
              ? ` | ${ignoreMinutesIfZero(availableResources.requiredMinutes)}`
              : ''
          }`
        : null,
    [availableResources?.requiredMinutes, projectName, teamName],
  );

  const { replaceTeamWithUser, replacementInProgress } = useReplaceTeamWithUser(
    {
      selectedReplacementUser,
      successHandler: onClose,
    },
  );

  const { subModalsVisibile, checkUserRoleAndReplaceTeam } =
    useCheckRoleAndReplaceUser({
      roleId,
      projectId,
      projectName,
      teamId,
      teamRoleName,
      selectedReplacementUser,
      replaceTeamWithUser,
    });

  const isDisabled = !selectedReplacementUser || replacementInProgress;

  return (
    <ModalWithStyledFooter
      visible={!subModalsVisibile}
      title={
        <ModalTitle
          primaryTitle={translate('resource_management.find_member.title')}
          secondaryTitle={secondaryTitle}
        />
      }
      onCancel={onClose}
      footer={
        <Footer>
          <Button
            type="text"
            onClick={onClose}
            disabled={replacementInProgress}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            type="primary"
            disabled={isDisabled}
            onClick={checkUserRoleAndReplaceTeam}
          >
            <FormattedMessage id="add" />
          </Button>
        </Footer>
      }
    >
      {isLoading || replacementInProgress ? (
        <ModalContentLoader
          loadingText={
            replacementInProgress
              ? translate(
                  'resource_management.actions.find_member.in_progress',
                  { div: (chunks) => <div>{chunks}</div> },
                )
              : undefined
          }
        />
      ) : (
        <FindMemberContent
          projectId={projectId}
          projectLookup={projectLookup}
          teamId={teamId}
          teamName={teamName}
          availableResources={availableResources}
          selectedReplacementUsers={
            selectedReplacementUser ? [selectedReplacementUser] : []
          }
          updateSelectedReplacementUsers={(_users) =>
            setSelectedReplacementUser(_users[0])
          }
        />
      )}
    </ModalWithStyledFooter>
  );
};
